<template>
  <article id="lotto-wrapper" class="contents-wrapper">
    <div id="lotto-create">
      <!-- 페이지 헤더 -->
      <div class="create-head">
        <h1 class="create-title">번호 생성</h1>
        <p class="create-sub">왼쪽에서 번호를 만들면 오른쪽에 결과와 역대 당첨 이력이 바로 표시됩니다.</p>
      </div>

      <div class="create-layout">
        <!-- ===== 왼쪽: 입력/생성 컨트롤 ===== -->
        <div class="cl-left">
          <!-- 종이 복권 불러오기 (자주 씀) -->
          <div class="section-card qr-card">
            <div class="section-title">종이 복권 불러오기</div>
            <p class="section-desc">매장에서 산 로또 용지의 QR을 읽어 구매 내역으로 저장합니다. 자동·수동·반자동 구분도 함께 기록돼요.</p>

            <button v-if="!isDesktop()" class="create-btn qr-btn" @click="openQRScanner">📷 카메라로 QR 스캔</button>
            <QRScannerDialog v-if="isScanning" @qrScanned="handleScannedData" @close="closeQRScanner" />

            <div class="qr-paste">
              <div class="qr-paste-label">QR 링크 직접 입력</div>
              <div class="qr-paste-row">
                <input
                  v-model="qrPasteUrl"
                  type="text"
                  class="qr-paste-input"
                  placeholder="QR을 열면 나오는 m.dhlottery.co.kr 링크를 붙여넣으세요"
                />
                <button class="create-btn qr-load-btn" :disabled="!qrPasteUrl.trim()" @click="loadQrFromPaste">불러오기</button>
              </div>
              <div v-if="qrPasteError" class="qr-paste-error">{{ qrPasteError }}</div>
            </div>

            <div v-if="qrBuyList.length > 0" class="result-area">
              <LottoResult v-if="qrLottoResult.drwNo > 0" :lottoResult="qrLottoResult" />
              <div v-else class="result-drw">{{ qrDrwNo }}회차</div>
              <div class="game-list">
                <div v-for="(gameBuy, idx) in qrBuyList" :key="'qr-' + idx" class="game-item">
                  <LottoBuyGame :lottoGame="gameBuy" :hideRank="true" />
                </div>
              </div>
              <button class="save-btn" @click="saveQRGame()">구매 저장</button>
            </div>
          </div>

          <!-- 번호 즐겨찾기 -->
          <div class="section-card favorite-section">
            <div class="section-title">번호 즐겨찾기</div>
            <div v-if="favorites.length === 0" class="result-empty compact">저장된 즐겨찾기가 없습니다</div>
            <div v-else class="favorite-list">
              <div v-for="fav in favorites" :key="'fav-' + fav.id" class="favorite-item">
                <div class="favorite-info">
                  <div class="favorite-name">{{ fav.favoriteName || '즐겨찾기' }}</div>
                  <div class="favorite-balls">
                    <div
                      v-for="(num, i) in fav.getNumbers()"
                      :key="'fb-' + fav.id + '-' + i"
                      class="fav-ball"
                      :class="getFixedColorClass(num)"
                    >{{ num }}</div>
                  </div>
                </div>
                <div class="favorite-actions">
                  <button class="fav-use-btn" @click="useFavorite(fav)">사용</button>
                  <button class="fav-delete-btn" @click="deleteFavoriteItem(fav.id)">삭제</button>
                </div>
              </div>
            </div>
          </div>

          <!-- 자동·전략 생성 -->
          <div class="section-card strategy-section">
            <div class="section-title">자동·전략 생성</div>
            <p class="section-lead">원하는 방식을 고르면 5게임을 자동으로 만들어 드려요.</p>
            <div class="strategy-group" v-for="group in strategyGroups" :key="group.label">
              <div class="strategy-group-label">{{ group.label }}</div>
              <div class="strategy-btn-list">
                <button
                  v-for="strategy in group.items"
                  :key="strategy.key"
                  class="strategy-btn"
                  :class="{ active: activeStrategy === strategy.key }"
                  @click="executeStrategy(strategy.key)"
                >
                  <span class="strategy-btn-title">{{ strategy.title }}</span>
                  <span class="strategy-btn-desc">{{ strategy.desc }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- 직접 고르기 (포함/제외) -->
          <div class="section-card fixed-section">
            <div class="section-title">직접 고르기</div>
            <p class="section-lead">
              꼭 넣고 싶은 번호는 <b>고정</b>(최대 5개), 빼고 싶은 번호는 <b>제외</b>로 지정하면
              나머지 자리를 랜덤으로 채워 5게임을 만듭니다.
            </p>
            <div class="fixed-mode-btns">
              <button :class="{ active: fixedMode === 'include' }" @click="fixedMode = 'include'">고정할 번호</button>
              <button :class="{ active: fixedMode === 'exclude' }" @click="fixedMode = 'exclude'">제외할 번호</button>
            </div>
            <div class="fixed-number-grid">
              <button
                v-for="n in 45"
                :key="'fn-' + n"
                class="fixed-number-btn"
                :class="[
                  getFixedColorClass(n),
                  { selected: fixedNumbers.includes(n) },
                  { excluded: excludeNumbers.includes(n) },
                ]"
                @click="toggleNumber(n)"
              >
                {{ n }}
                <span v-if="excludeNumbers.includes(n)" class="exclude-x">✕</span>
              </button>
            </div>
            <div class="fixed-info-row">
              <div class="fixed-selected" v-if="fixedNumbers.length > 0">
                고정: {{ fixedNumbers.sort((a, b) => a - b).join(', ') }}
              </div>
              <div class="exclude-selected" v-if="excludeNumbers.length > 0">
                제외: {{ excludeNumbers.sort((a, b) => a - b).join(', ') }}
              </div>
            </div>
            <button
              class="create-btn"
              :disabled="fixedNumbers.length === 0 && excludeNumbers.length === 0"
              @click="createFixedGame"
            >
              생성 (고정 {{ fixedNumbers.length }}개, 제외 {{ excludeNumbers.length }}개)
            </button>
          </div>
        </div>

        <!-- ===== 오른쪽: 결과 ===== -->
        <div class="cl-right" :class="{ 'pane-mode': simResult !== null || simLoading }">
          <!-- 생성된 번호 -->
          <div class="section-card result-panel">
            <div class="section-title">생성된 번호</div>
            <template v-if="currentGameList.length > 0">
              <div class="strategy-result-header">
                <div class="result-title-group">
                  <span class="result-strategy-name">{{ currentResultName }}</span>
                  <span class="result-meta">{{ lastDrawNo }}회차 응모 · {{ currentGameList.length }}게임 · {{ formatKRW(currentGameList.length * 1000) }}</span>
                </div>
              </div>
              <div class="game-list">
                <div v-for="(gameBuy, idx) in currentGameList" :key="'cur-' + idx" class="game-item">
                  <LottoBuyGame :lottoGame="gameBuy" :hideRank="true" />
                </div>
              </div>
              <div class="strategy-action-btns">
                <button class="save-btn" @click="saveCurrent">구매 저장</button>
                <button class="save-btn fav-outline" @click="addCurrentToFavorite">⭐ 즐겨찾기</button>
              </div>
            </template>
            <div v-else class="result-empty">
              <div class="result-empty-icon">🎱</div>
              <div class="result-empty-text">왼쪽에서 번호를 생성하면 여기에 나타납니다</div>
            </div>
          </div>

          <!-- 역대 당첨 이력 조회 중 -->
          <div v-if="simLoading && simResult === null" class="section-card sim-loading-card">
            <div class="sim-loading-spinner"></div>
            <span>역대 당첨 이력 조회 중…</span>
          </div>

          <!-- 역대 당첨 이력 판정 결과 -->
          <div v-if="simResult !== null" class="section-card simulation-section">
            <div class="section-title">역대 당첨 이력 판정</div>
            <p class="section-desc">생성된 번호를 <b>역대 {{ simResult.totalDraws }}회차</b> 전체에 대입했을 때의 결과예요. (실제 당첨과 무관한 참고용)</p>

            <div class="sim-summary">
              <div class="sim-item" v-for="(count, rank) in simResult.rankCounts" :key="'sim-' + rank">
                <div class="sim-rank" :class="'sim-rank-' + rank">{{ rank === '6' ? '낙첨' : rank + '등' }}</div>
                <div class="sim-count">{{ count }}회</div>
              </div>
            </div>

            <div class="sim-hits" v-if="simResult.hits.length > 0">
              <div class="sim-hits-title">당첨된 회차 ({{ simResult.hits.length }}건)</div>
              <div class="sim-hits-list">
                <div v-for="(hit, idx) in simResult.hits" :key="'hit-' + idx" class="sim-hit-item">
                  <span class="sim-hit-rank" :class="'sim-rank-' + hit.rank">{{ hit.rank }}등</span>
                  <span class="sim-hit-drw">{{ hit.drwNo }}회차</span>
                  <span class="sim-hit-nums">{{ hit.numbers.join(', ') }}</span>
                  <span class="sim-hit-prize" v-if="hit.prize > 0">{{ formatKRW(hit.prize) }}</span>
                </div>
              </div>
            </div>
            <div class="sim-hits-empty" v-else>역대 회차 중 3등 이상 당첨된 기록은 없어요.</div>

            <div class="sim-total">
              <div>총 대입 게임: {{ getMoneyFormat(simResult.totalGames) }}</div>
              <div>가상 투자: {{ formatKRW(simResult.totalGames * 1000) }}</div>
              <div>가상 당첨금: {{ formatKRW(simResult.totalPrize) }}</div>
              <div class="sim-roi" :class="{ positive: simResult.totalPrize > simResult.totalGames * 1000 }">
                회수율: {{ ((simResult.totalPrize / (simResult.totalGames * 1000)) * 100).toFixed(1) }}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import { LottoGame, LottoGameBuyResponse, LottoResultResponse, LottoFavorite } from "../class";
import QRScannerDialog from "@/common/components/QRScannerDialog.vue";
import LottoBuyGame from "@/lotto/components/LottoBuyGame.vue";
import LottoResult from "../components/LottoResult.vue";
import * as lottoUtil from "../utils/LottoUtils";

interface StrategyDef {
  key: string;
  title: string;
  desc: string;
}

interface SimulationHit {
  drwNo: number;
  rank: number;
  prize: number;
  numbers: number[];
}

interface SimulationResult {
  totalDraws: number;
  totalGames: number;
  totalPrize: number;
  rankCounts: Record<string, number>;
  hits: SimulationHit[];
}

export default defineComponent({
  name: "LottoCreateView",
  components: { QRScannerDialog, LottoBuyGame, LottoResult },
  mixins: [UtilMixin],

  data() {
    return {
      isScanning: false,
      qrCodeData: "" as string,
      qrPasteUrl: "" as string,
      qrPasteError: "" as string,
      qrDrwNo: 0 as number | undefined,
      qrLottoResult: new LottoResultResponse(),
      qrBuyList: [] as LottoGameBuyResponse[],
      // 개별 전략 (그룹화 — 이해하기 쉬운 설명)
      strategyGroups: [
        {
          label: "기본",
          items: [
            { key: "mixStandard", title: "혼합 추천", desc: "빈도·동반 데이터를 고루 섞은 조합" },
            { key: "random", title: "완전 랜덤", desc: "조건 없이 무작위로 뽑기" },
          ],
        },
        {
          label: "통계 기반",
          items: [
            { key: "mixShowCount", title: "출현빈도 혼합", desc: "자주·드물게 나온 번호를 섞음" },
            { key: "showCountDesc", title: "많이 나온 번호", desc: "출현빈도 상위 번호 위주" },
            { key: "showCountAsc", title: "적게 나온 번호", desc: "출현빈도 하위 번호 위주" },
            { key: "accompanyDesc", title: "자주 함께 나온 조합", desc: "동반 출현이 많은 번호 묶음" },
            { key: "accompanyAsc", title: "드물게 함께 나온 조합", desc: "동반 출현이 적은 번호 묶음" },
            { key: "averageDrwNo", title: "자리별 평균값", desc: "각 자리 평균에 가까운 번호" },
          ],
        },
      ] as { label: string; items: StrategyDef[] }[],
      activeStrategy: "" as string,
      strategyResult: [] as LottoGameBuyResponse[],
      // 고정/제외 번호
      fixedMode: "include" as "include" | "exclude",
      fixedNumbers: [] as number[],
      excludeNumbers: [] as number[],
      fixedResult: [] as LottoGameBuyResponse[],
      // 시뮬레이션
      simResult: null as SimulationResult | null,
      simLoading: false,
      cachedDraws: [] as LottoResultResponse[], // 역대 회차 캐시 (반복 조회 방지)
      // 즐겨찾기
      favorites: [] as LottoFavorite[],
    };
  },
  computed: {
    lastDrawNo(): number {
      return lottoUtil.getNowDrawNo();
    },
    activeStrategyName(): string {
      const all = this.strategyGroups.flatMap((g) => g.items);
      const found = all.find((s) => s.key === this.activeStrategy);
      return found ? found.title : "";
    },
    currentGameList(): LottoGameBuyResponse[] {
      if (this.strategyResult.length > 0) return this.strategyResult;
      if (this.fixedResult.length > 0) return this.fixedResult;
      return [];
    },
    currentResultName(): string {
      if (this.strategyResult.length > 0) return this.activeStrategyName;
      if (this.fixedResult.length > 0) return "직접 고른 번호";
      return "";
    },
  },

  methods: {
    async executeStrategy(key: string) {
      this.activeStrategy = key;
      this.simResult = null;
      this.fixedResult = [];
      const createApi = this.getApi().lotto().createApi();
      let games: LottoGame[] = [];
      switch (key) {
        case "mixStandard":
          games = await createApi.getMixStandard();
          break;
        case "mixShowCount":
          games = await createApi.getMixShowCount();
          break;
        case "random":
          games = await createApi.getRandom(5);
          break;
        case "showCountDesc":
          games = await createApi.getShowCountDesc(5);
          break;
        case "showCountAsc":
          games = await createApi.getShowCountAsc(5);
          break;
        case "accompanyDesc":
          games = await createApi.getAccompanyCountDesc();
          break;
        case "accompanyAsc":
          games = await createApi.getAccompanyCountAsc();
          break;
        case "averageDrwNo":
          games = await createApi.getAverageDrwNo();
          break;
      }
      this.strategyResult = games.map((g: LottoGame) => lottoUtil.createLottoGameBuyResponseNotDraw(g));
      // 생성 즉시 역대 당첨 이력 자동 조회
      this.runSimulation(this.strategyResult);
    },
    async saveStrategyGame() {
      if (this.strategyResult.length === 0) return;

      if (!this.getCore().isLogin()) {
        if (confirm("로그인 후 가능 합니다. 로그인 하시겠습니까?")) {
          this.getRouterUtil().moveTo("/user/login");
        }
        return;
      }

      if (!confirm("해당 목록을 구매 및 저장할까요?")) return;

      const buyList = this.strategyResult.map((item) => {
        const game = new LottoGame().fromLottoGameBuyResponse(item);
        game.drwNo = this.lastDrawNo;
        return game;
      });
      const res = await this.getApi().lotto().saveLottoGameBuyList(buyList);
      if (res.isSuccess()) {
        alert("저장 성공");
        this.strategyResult = [];
        this.activeStrategy = "";
      } else {
        alert("저장 실패\n사유 : " + res.message);
      }
    },

    // 통합 저장/판정 (현재 생성된 목록 기준)
    saveCurrent() {
      if (this.strategyResult.length > 0) this.saveStrategyGame();
      else if (this.fixedResult.length > 0) this.saveFixedGame();
    },
    simulateCurrent() {
      this.runSimulation(this.currentGameList);
    },
    addCurrentToFavorite() {
      if (this.currentGameList.length > 0) this.addToFavorite(this.currentGameList[0]);
    },

    // 시뮬레이션
    async simulateStrategyGame() {
      await this.runSimulation(this.strategyResult);
    },
    async simulateFixedGame() {
      await this.runSimulation(this.fixedResult);
    },
    async runSimulation(games: LottoGameBuyResponse[]) {
      if (games.length === 0) return;
      this.simLoading = true;
      try {
        // 역대 전 회차 조회 (최초 1회만, 이후 캐시 재사용)
        if (this.cachedDraws.length === 0) {
          const resultRes = await this.getApi().lotto().getAllLottoResults(1, 100000);
          if (!resultRes || !Array.isArray(resultRes.content)) return;
          this.cachedDraws = resultRes.content.map((item: any) => {
            const r = new LottoResultResponse();
            r.init(item);
            return r;
          });
        }
        const draws: LottoResultResponse[] = this.cachedDraws;

        const rankCounts: Record<string, number> = { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0, "6": 0 };
        let totalPrize = 0;
        const hits: SimulationHit[] = [];

        for (const draw of draws) {
          if (draw.drwNo <= 0) continue;
          for (const gameBuy of games) {
            const g = new LottoGame();
            g.init(draw.drwNo, gameBuy.drwtNo1, gameBuy.drwtNo2, gameBuy.drwtNo3, gameBuy.drwtNo4, gameBuy.drwtNo5, gameBuy.drwtNo6);
            const result = lottoUtil.createLottoGameBuyResponse(g, draw);
            const rank = String(result.rank);
            if (rankCounts[rank] !== undefined) {
              rankCounts[rank]++;
            }
            totalPrize += result.prize;
            if (result.rank >= 1 && result.rank <= 5) {
              hits.push({
                drwNo: draw.drwNo,
                rank: result.rank,
                prize: result.prize,
                numbers: [gameBuy.drwtNo1, gameBuy.drwtNo2, gameBuy.drwtNo3, gameBuy.drwtNo4, gameBuy.drwtNo5, gameBuy.drwtNo6],
              });
            }
          }
        }

        // 등수 높은 순 → 최신 회차 순
        hits.sort((a, b) => a.rank - b.rank || b.drwNo - a.drwNo);

        this.simResult = {
          totalDraws: draws.length,
          totalGames: draws.length * games.length,
          totalPrize,
          rankCounts,
          hits,
        };
      } catch (e) {
        console.error("Simulation error:", e);
      } finally {
        this.simLoading = false;
      }
    },
    formatKRW(amount: number): string {
      if (amount >= 100000000) {
        return (amount / 100000000).toFixed(1) + "억원";
      } else if (amount >= 10000) {
        return (amount / 10000).toFixed(0) + "만원";
      }
      return amount.toLocaleString() + "원";
    },

    // 고정/제외 번호 관련
    getFixedColorClass(n: number): string {
      if (n >= 1 && n <= 10) return "fc-yellow";
      if (n > 10 && n <= 20) return "fc-blue";
      if (n > 20 && n <= 30) return "fc-red";
      if (n > 30 && n <= 40) return "fc-gray";
      if (n > 40 && n <= 45) return "fc-green";
      return "";
    },
    toggleNumber(n: number) {
      if (this.fixedMode === "include") {
        const exIdx = this.excludeNumbers.indexOf(n);
        if (exIdx >= 0) this.excludeNumbers.splice(exIdx, 1);
        const idx = this.fixedNumbers.indexOf(n);
        if (idx >= 0) {
          this.fixedNumbers.splice(idx, 1);
        } else {
          if (this.fixedNumbers.length >= 5) {
            alert("최대 5개까지 고정할 수 있습니다");
            return;
          }
          this.fixedNumbers.push(n);
        }
      } else {
        const fIdx = this.fixedNumbers.indexOf(n);
        if (fIdx >= 0) this.fixedNumbers.splice(fIdx, 1);
        const idx = this.excludeNumbers.indexOf(n);
        if (idx >= 0) {
          this.excludeNumbers.splice(idx, 1);
        } else {
          this.excludeNumbers.push(n);
        }
      }
    },
    createFixedGame() {
      if (this.fixedNumbers.length === 0 && this.excludeNumbers.length === 0) return;
      if (this.fixedNumbers.length > 5) return;
      this.simResult = null;
      this.strategyResult = [];
      this.activeStrategy = "";
      const allExclude = [...this.fixedNumbers, ...this.excludeNumbers];
      const pool = Array.from({ length: 45 }, (_, i) => i + 1).filter((n) => !allExclude.includes(n));

      if (pool.length < 6 - this.fixedNumbers.length) {
        alert("제외 번호가 너무 많아 게임을 생성할 수 없습니다");
        return;
      }

      const games: LottoGame[] = [];
      for (let i = 0; i < 5; i++) {
        const fixed = [...this.fixedNumbers].sort((a, b) => a - b);
        const remaining = this.pickRandom(pool, 6 - fixed.length);
        const allNums = [...fixed, ...remaining].sort((a, b) => a - b);
        const game = new LottoGame();
        game.init(0, allNums[0], allNums[1], allNums[2], allNums[3], allNums[4], allNums[5]);
        games.push(game);
      }
      this.fixedResult = games.map((g) => lottoUtil.createLottoGameBuyResponseNotDraw(g));
      // 생성 즉시 역대 당첨 이력 자동 조회
      this.runSimulation(this.fixedResult);
    },
    pickRandom(pool: number[], count: number): number[] {
      const copy = [...pool];
      const result: number[] = [];
      for (let i = 0; i < count; i++) {
        const randIdx = Math.floor(Math.random() * copy.length);
        result.push(copy[randIdx]);
        copy.splice(randIdx, 1);
      }
      return result;
    },
    async saveFixedGame() {
      if (this.fixedResult.length === 0) return;

      if (!this.getCore().isLogin()) {
        if (confirm("로그인 후 가능 합니다. 로그인 하시겠습니까?")) {
          this.getRouterUtil().moveTo("/user/login");
        }
        return;
      }

      if (!confirm("해당 목록을 구매 및 저장할까요?")) return;

      const buyList = this.fixedResult.map((item) => {
        const game = new LottoGame().fromLottoGameBuyResponse(item);
        game.drwNo = this.lastDrawNo;
        return game;
      });
      const res = await this.getApi().lotto().saveLottoGameBuyList(buyList);
      if (res.isSuccess()) {
        alert("저장 성공");
        this.fixedResult = [];
      } else {
        alert("저장 실패\n사유 : " + res.message);
      }
    },

    // 즐겨찾기
    getGameNumbers(game: LottoGameBuyResponse): number[] {
      return [game.drwtNo1, game.drwtNo2, game.drwtNo3, game.drwtNo4, game.drwtNo5, game.drwtNo6];
    },
    async loadFavorites() {
      if (!this.getCore().isLogin()) return;
      try {
        this.favorites = await this.getApi().lotto().getFavorites();
      } catch (e) {
        console.error("loadFavorites error:", e);
      }
    },
    async addToFavorite(game: LottoGameBuyResponse) {
      if (!this.getCore().isLogin()) {
        if (confirm("로그인 후 가능 합니다. 로그인 하시겠습니까?")) {
          this.getRouterUtil().moveTo("/user/login");
        }
        return;
      }
      const name = prompt("즐겨찾기 이름을 입력하세요", "내 번호");
      if (name === null) return;
      try {
        const nums = this.getGameNumbers(game);
        await this.getApi().lotto().saveFavorite(name || "내 번호", nums);
        await this.loadFavorites();
      } catch (e) {
        console.error("addToFavorite error:", e);
      }
    },
    useFavorite(fav: LottoFavorite) {
      this.fixedNumbers = fav.getNumbers();
      this.fixedMode = "include";
      this.excludeNumbers = [];
    },
    async deleteFavoriteItem(id: number) {
      if (!confirm("즐겨찾기를 삭제하시겠습니까?")) return;
      try {
        await this.getApi().lotto().deleteFavorite(id);
        await this.loadFavorites();
      } catch (e) {
        console.error("deleteFavoriteItem error:", e);
      }
    },

    openQRScanner() {
      this.isScanning = true;
    },

    async loadQrFromPaste() {
      const url = this.qrPasteUrl.trim();
      this.qrPasteError = "";
      if (!url) return;
      if (!/v=\d/.test(url)) {
        this.qrPasteError = "올바른 동행복권 QR 링크가 아닙니다. (m.dhlottery.co.kr/...v=... 형식)";
        return;
      }
      const parsed = this.parseLottoQrUrl(url);
      if (!parsed || !parsed.drwNo || parsed.games.length === 0) {
        this.qrPasteError = "링크에서 게임 번호를 읽지 못했습니다. 링크 전체를 다시 붙여넣어 주세요.";
        return;
      }
      this.qrCodeData = url;
      await this.processQrData(url);
    },
    closeQRScanner() {
      this.isScanning = false;
    },

    async handleScannedData(data: string) {
      this.qrCodeData = data;
      await this.processQrData(data);
    },

    async processQrData(data: string) {
      const result = this.parseLottoQrUrl(data);
      const qrGames = this.createQrLottoGame(result);
      this.qrDrwNo = result?.drwNo;

      if (!result?.drwNo) return;
      const lottoResultRes = await this.getApi().lotto().getLottoResult(result.drwNo, false);

      this.qrLottoResult.init(lottoResultRes);
      this.qrBuyList = qrGames.map((game) =>
        lottoResultRes.drwNo > 0
          ? lottoUtil.createLottoGameBuyResponse(game, lottoResultRes)
          : lottoUtil.createLottoGameBuyResponseNotDraw(game),
      );
    },

    parseLottoQrUrl(url: string) {
      const vIndex = url.indexOf("v=");
      if (vIndex === -1) return null;

      const dataStr = url.substring(vIndex + 2);
      const drwNoMatch = dataStr.match(/^\d+/);
      if (!drwNoMatch) return null;
      const drwNo = parseInt(drwNoMatch[0], 10);
      const rest = dataStr.slice(drwNoMatch[0].length);

      const games: { numbers: number[]; type: string }[] = [];
      const re = /([a-zA-Z]?)(\d{12})/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(rest)) !== null) {
        const letter = (m[1] || "").toLowerCase();
        const g = m[2];
        const numbers = Array.from({ length: 6 }, (_, j) => parseInt(g.slice(j * 2, j * 2 + 2), 10));
        if (numbers.every((n) => n >= 1 && n <= 45) && new Set(numbers).size === 6) {
          games.push({ numbers, type: this.qrLetterToBuyType(letter) });
        }
      }

      return { drwNo, games, ext: null };
    },
    qrLetterToBuyType(letter: string): string {
      if (letter === "m") return "MANUAL";
      if (letter === "s") return "SEMI_AUTO";
      return "AUTO";
    },
    createQrLottoGame(result: { drwNo: number; games: { numbers: number[]; type: string }[] } | null) {
      if (!result) return [];
      return result.games.map((game) => {
        const g = new LottoGame();
        g.init(result.drwNo, game.numbers[0], game.numbers[1], game.numbers[2], game.numbers[3], game.numbers[4], game.numbers[5]);
        g.buyType = game.type;
        return g;
      });
    },
    clearQrData() {
      this.qrCodeData = "";
      this.qrPasteUrl = "";
      this.qrPasteError = "";
      this.qrDrwNo = 0;
      this.qrLottoResult = new LottoResultResponse();
      this.qrBuyList = [];
    },
    async saveQRGame() {
      if (!this.getCore().isLogin()) {
        if (confirm("로그인 후 가능 합니다. 로그인 하시겠습니까?")) {
          const nextRoute = this.getRouterUtil().getCreateNextRoute({
            qrData: this.qrCodeData,
          });
          this.getRouterUtil().moveTo("/user/login", nextRoute);
        }
        return;
      }

      if (!confirm("해당 목록을 구매 및 저장할까요?")) return;

      const buyList = this.qrBuyList.map((item) => new LottoGame().fromLottoGameBuyResponse(item));
      const res = await this.getApi().lotto().saveLottoGameBuyList(buyList);
      if (res.isSuccess()) {
        alert("저장 성공");
        this.clearQrData();
      } else {
        alert("저장 실패\n사유 : " + res.message);
      }
    },
  },
  created() {
    this.loadFavorites();
  },
  mounted() {
    const nextRoute = this.getRouterUtil().getCurrentNextRoute();
    if (nextRoute && nextRoute.qrData) {
      this.qrCodeData = nextRoute.qrData;
      this.handleScannedData(this.qrCodeData);
    }
  },
});
</script>
